import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI, unwrapVendors, unwrapBills } from '../lib/api';
import BillAttachmentsViewer, { BillRowThumb } from '../components/BillAttachmentsViewer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText, Search, Filter, Eye, Calendar, Building2,
  ChevronLeft, ChevronRight, Image, AlertCircle, Loader2, Scan, Shield, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentProofOCR from '../components/PaymentProofOCR';
import PaymentAllocationDialog from '../components/PaymentAllocationDialog';
import { DeletionRequestModal, ActiveDeleteRequestBadge } from '../components/DeletionRequestModal';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const StatusBadge = ({ status }) => {
  const variants = {
    Unpaid: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    Partial: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    Paid: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    OVERDUE: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  };

  return (
    <Badge variant="outline" className={`${variants[status] || variants.Unpaid} font-medium`}>
      {status}
    </Badge>
  );
};

const BillsPage = () => {
  const { selectedOrg } = useApp();
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedBill, setSelectedBill] = useState(null);
  const [vendorMap, setVendorMap] = useState({});
  const [vendors, setVendors] = useState([]);
  const [mappingTax, setMappingTax] = useState(false);
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);
  const periodKey = JSON.stringify(periodState.apiParams);
  useEffect(() => { setPage(0); }, [periodKey]);
  const [showOCRDialog, setShowOCRDialog] = useState(false);
  const [showExclusionModal, setShowExclusionModal] = useState(false);
  const [exclusionBill, setExclusionBill] = useState(null);
  // Unified allocation dialog state (launched after OCR payment creation)
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [ocrPayment, setOcrPayment] = useState(null);
  const limit = 20;

  // Check if bill has unclear tax (total_tax exists but no GST/VAT breakdown)
  const hasUnclearTax = (bill) => {
    if (!bill) return false;
    const hasBreakdown = (bill.gst_amount > 0) || (bill.vat_amount > 0) || (bill.tcs_amount > 0);
    const hasTotalTax = bill.total_tax_amount > 0;
    const alreadyMapped = bill.tax_mapping_type;
    return hasTotalTax && !hasBreakdown && !alreadyMapped;
  };

  const handleTaxMapping = async (taxType) => {
    if (!selectedBill) return;
    setMappingTax(true);
    try {
      await accountsAPI.mapBillTax(selectedBill.bill_id, { tax_type: taxType });
      // Refresh bills and update selected bill
      await loadBills();
      // Update selected bill with new tax type
      setSelectedBill(prev => ({
        ...prev,
        tax_mapping_type: taxType,
        gst_amount: taxType === 'GST' ? prev.total_tax_amount : 0,
        vat_amount: taxType === 'VAT' ? prev.total_tax_amount : 0,
        tcs_amount: taxType === 'TCS' ? prev.total_tax_amount : 0,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
    setMappingTax(false);
  };

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (selectedOrg) {
      loadVendors();
      loadBills();
    }
  }, [selectedOrg, page, statusFilter, periodState.apiParams, debouncedSearch]);

  const loadVendors = async () => {
    try {
      const response = await accountsAPI.getVendors(selectedOrg.id);
      // Truth Contract: vendor endpoint may return an envelope or an
      // array. Always coerce to an array before iterating so we never
      // throw `vendors.forEach is not a function`.
      const list = unwrapVendors(response.data);
      setVendors(list);
      const map = {};
      list.forEach(v => {
        // Use vendor_id as key (from Vendor Engine)
        map[v.vendor_id || v.id] = v;
      });
      setVendorMap(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendors([]);
      setVendorMap({});
    }
  };

  const loadBills = async () => {
    if (!selectedOrg) return;
    setLoading(true);
    try {
      const params = {
        org_id: selectedOrg.id,
        skip: page * limit,
        limit,
      };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch && debouncedSearch.trim()) params.search = debouncedSearch.trim();
      Object.assign(params, periodState.apiParams);

      const response = await accountsAPI.getBills(params);
      setResolvedPeriod(response.data);
      // Envelope-tolerant: the backend always returns
      // `{state, source, bills:[...], total, ...}` but historically some
      // call paths returned a flat array — stay defensive.
      setBills(unwrapBills(response.data));
      setTotal(
        typeof response.data?.total === 'number'
          ? response.data.total
          : unwrapBills(response.data).length
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setBills([]);
      setTotal(0);
    }
    setLoading(false);
  };

  // Search is now server-side — bills already filtered by backend
  const filteredBills = bills;

  const totalPages = Math.ceil(total / limit);

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bills-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Bills</h1>
          <p className="text-muted-foreground mt-1">
            Manage vendor bills and track payments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowOCRDialog(true)}
            data-testid="ocr-payment-btn"
          >
            <Scan className="w-4 h-4" />
            Scan Payment Proof
          </Button>
          <Badge variant="outline" className="font-mono">
            {total} bills
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by bill number or vendor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="bill-search"
              />
            </div>
            <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
            <div className="flex gap-2">
              {['', 'Unpaid', 'Partial', 'Paid', 'OVERDUE'].map((status) => (
                <Button
                  key={status || 'all'}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setStatusFilter(status); setPage(0); }}
                  className={statusFilter === status ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid={`filter-${status || 'all'}`}
                >
                  {status || 'All'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bills Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={`skeleton-${i}`} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="font-manrope">Bill Number</TableHead>
                  <TableHead className="font-manrope">Vendor</TableHead>
                  <TableHead className="font-manrope">Date</TableHead>
                  <TableHead className="font-manrope text-right">Amount</TableHead>
                  <TableHead className="font-manrope text-right">Paid</TableHead>
                  <TableHead className="font-manrope text-right">Balance</TableHead>
                  <TableHead className="font-manrope">Status</TableHead>
                  <TableHead className="font-manrope">Image</TableHead>
                  <TableHead className="font-manrope">Sync</TableHead>
                  <TableHead className="font-manrope w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBills.map((bill) => {
                  // Stable key: bill_id (canonical from EC) → id → bill_number → idx fallback.
                  // Without this, EC payloads that omit `id` produce duplicate undefined
                  // keys and React fires a console warning; downstream selectors break.
                  const rowKey = bill.bill_id || bill.id || bill.bill_number;
                  return (
                  <TableRow 
                    key={rowKey} 
                    className="border-border/50 hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedBill(bill)}
                    data-testid={`bill-row-${rowKey}`}
                  >
                    <TableCell className="font-mono text-sm">{bill.bill_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        {vendorMap[bill.vendor_id]?.name || bill.vendor_name || 'Unknown'}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{formatDate(bill.bill_date)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(bill.total_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(bill.paid_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-400">{formatCurrency(bill.balance_amount)}</TableCell>
                    <TableCell><StatusBadge status={bill.status} /></TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <BillRowThumb billId={bill.bill_id || bill.id} />
                    </TableCell>
                    <TableCell>
                      {bill.sync_status === 'NOT_IN_EXPENSE_APP' && (
                        <Badge variant="outline" className="text-[9px] border-red-500/30 text-red-400 bg-red-500/10" data-testid={`stale-badge-${bill.bill_id || bill.id}`}>
                          NO LONGER IN EXPENSE APP
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {filteredBills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No bills found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Bill Detail Dialog */}
      <Dialog open={!!selectedBill} onOpenChange={() => setSelectedBill(null)}>
        <DialogContent className="max-w-2xl bg-card border-border" data-testid="bill-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Bill Details</DialogTitle>
          </DialogHeader>
          {selectedBill && (
            <div className="space-y-6">
              {selectedBill.sync_status === 'NOT_IN_EXPENSE_APP' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="bill-stale-warning">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400">This bill is no longer present in the Expense App. It may have been deleted or removed at source.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Bill Number</p>
                  <p className="font-mono font-medium">{selectedBill.bill_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-medium">{vendorMap[selectedBill.vendor_id]?.name || selectedBill.vendor_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bill Date</p>
                  <p className="font-mono">{formatDate(selectedBill.bill_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className="font-mono">{formatDate(selectedBill.due_date)}</p>
                </div>
              </div>

              <div className="border-t border-border/50 pt-4">
                <h4 className="font-manrope font-medium mb-3">Tax Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Taxable Amount</span>
                    <span className="font-mono">{formatCurrency(selectedBill.taxable_amount || selectedBill.subtotal_amount)}</span>
                  </div>
                  {selectedBill.gst_amount > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CGST ({selectedBill.gst_percent ? selectedBill.gst_percent / 2 : 0}%)</span>
                        <span className="font-mono">{formatCurrency(selectedBill.gst_amount / 2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">SGST ({selectedBill.gst_percent ? selectedBill.gst_percent / 2 : 0}%)</span>
                        <span className="font-mono">{formatCurrency(selectedBill.gst_amount / 2)}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span className="text-muted-foreground">Total GST ({selectedBill.gst_percent || 0}%)</span>
                        <span className="font-mono text-emerald-400">{formatCurrency(selectedBill.gst_amount)}</span>
                      </div>
                    </>
                  )}
                  {selectedBill.vat_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">VAT ({selectedBill.vat_percent || 0}%)</span>
                      <span className="font-mono text-orange-400">{formatCurrency(selectedBill.vat_amount)}</span>
                    </div>
                  )}
                  {selectedBill.tcs_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TCS ({selectedBill.tcs_percent || 0}%)</span>
                      <span className="font-mono text-purple-400">{formatCurrency(selectedBill.tcs_amount)}</span>
                    </div>
                  )}
                  {/* Show tax mapping UI when tax is unclear */}
                  {hasUnclearTax(selectedBill) && (
                    <div className="col-span-2 mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-400">Tax Type Unclear</p>
                          <p className="text-xs text-muted-foreground">
                            Total tax: {formatCurrency(selectedBill.total_tax_amount)} — Please classify this tax:
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400"
                          onClick={() => handleTaxMapping('GST')}
                          disabled={mappingTax}
                        >
                          {mappingTax ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                          GST
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20 text-orange-400"
                          onClick={() => handleTaxMapping('VAT')}
                          disabled={mappingTax}
                        >
                          VAT
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-400"
                          onClick={() => handleTaxMapping('TCS')}
                          disabled={mappingTax}
                        >
                          TCS
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-zinc-500/10 border-zinc-500/30 hover:bg-zinc-500/20 text-zinc-400"
                          onClick={() => handleTaxMapping('NONE')}
                          disabled={mappingTax}
                        >
                          None/Exempt
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Show mapped tax type badge */}
                  {selectedBill.tax_mapping_type && (
                    <div className="col-span-2 flex justify-between items-center">
                      <span className="text-muted-foreground text-xs">Mapped as:</span>
                      <Badge variant="outline" className={
                        selectedBill.tax_mapping_type === 'GST' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                        selectedBill.tax_mapping_type === 'VAT' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                        selectedBill.tax_mapping_type === 'TCS' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                        'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                      }>
                        {selectedBill.tax_mapping_type}
                      </Badge>
                    </div>
                  )}
                  {/* No tax at all */}
                  {(!selectedBill.gst_amount || selectedBill.gst_amount === 0) && 
                   (!selectedBill.vat_amount || selectedBill.vat_amount === 0) && 
                   (!selectedBill.tcs_amount || selectedBill.tcs_amount === 0) &&
                   (!selectedBill.total_tax_amount || selectedBill.total_tax_amount === 0) &&
                   !selectedBill.tax_mapping_type && (
                    <div className="col-span-2 text-muted-foreground text-center py-2">
                      No tax data available
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-border/50 pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="text-2xl font-mono font-bold">{formatCurrency(selectedBill.total_amount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Balance</p>
                    <p className="text-xl font-mono font-semibold text-amber-400">{formatCurrency(selectedBill.balance_amount)}</p>
                  </div>
                </div>
              </div>

              {selectedBill.is_trial_bill && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                  Trial Bill
                </Badge>
              )}

              {/* Bill image(s) — proxied through Accounts from EC.
                  Frontend never sees EC service tokens. Supports multi-page. */}
              <div className="border-t border-border/50 pt-4">
                <BillAttachmentsViewer billId={selectedBill.bill_id} />
              </div>

              {/* Active deletion request badge */}
              <ActiveDeleteRequestBadge entityType="EXPENSE_EXCLUSION" entityId={selectedBill.bill_id} />

              {/* Request Exclusion button */}
              {!selectedBill.is_excluded_in_accounts && (
                <div className="border-t border-border/50 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs text-amber-400 border-amber-400/30 hover:bg-amber-400/10 gap-2"
                    onClick={() => { setExclusionBill(selectedBill); setShowExclusionModal(true); }}
                    data-testid="request-exclusion-btn"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Request Exclusion from Accounts
                  </Button>
                </div>
              )}
              {selectedBill.is_excluded_in_accounts && (
                <div className="border-t border-border/50 pt-4">
                  <Badge variant="outline" className="bg-zinc-400/10 text-zinc-400 border-zinc-400/20 text-xs">
                    Excluded from Accounts
                  </Badge>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Proof OCR Dialog */}
      <PaymentProofOCR
        open={showOCRDialog}
        onOpenChange={setShowOCRDialog}
        vendors={vendors}
        onPaymentCreated={(paymentData) => {
          // After OCR creates a payment, open the unified allocation dialog
          if (paymentData?.payment_id && paymentData?.vendor_id) {
            setOcrPayment({
              payment_id: paymentData.payment_id,
              amount: paymentData.amount || 0,
              unallocated_amount: paymentData.unallocated_amount || paymentData.amount || 0,
              vendor_id: paymentData.vendor_id,
            });
            setAllocationDialogOpen(true);
          }
          loadBills();
        }}
      />

      {/* Unified Payment Allocation Dialog (launched after OCR) */}
      <PaymentAllocationDialog
        open={allocationDialogOpen}
        onOpenChange={setAllocationDialogOpen}
        payment={ocrPayment}
        vendorId={ocrPayment?.vendor_id}
        onComplete={loadBills}
      />

      {/* Deletion Request Modal for Expense Exclusion */}
      <DeletionRequestModal
        open={showExclusionModal}
        onClose={() => { setShowExclusionModal(false); setExclusionBill(null); }}
        entityType="EXPENSE_EXCLUSION"
        entityId={exclusionBill?.bill_id || ''}
        actionType="EXCLUDE_IN_ACCOUNTS"
        entityLabel={`Expense: ${vendorMap[exclusionBill?.vendor_id]?.name || exclusionBill?.vendor_name || 'Unknown'} — ${exclusionBill?.bill_number || ''} (${formatCurrency(exclusionBill?.total_amount)})`}
        onCreated={() => { setSelectedBill(null); loadBills(); }}
      />
    </div>
  );
};

export default BillsPage;
